/**
 * Moving a unit, and walking one along a standing goto order.
 *
 * These live apart from the command pipeline on purpose. The commands module needs
 * them (a player clicking a tile) and so does the turn engine (a standing order
 * running at the start of a turn). Putting them in either of those files would make
 * the two import each other, and a circular ES module import works right up until
 * somebody changes the load order and one half is silently undefined.
 *
 * Everything here assumes it has already been told the move is legal. Validation is
 * the command pipeline's job; this is the half that cannot fail.
 */

import { mayEnter } from '../diplomacy/borders.js';
import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { findPath, type PathOptions } from '../path/astar.js';
import { canAfford, IMPASSABLE, moveCost, movesSpent } from '../path/movement.js';
import { neighborsByDirection } from '../state/grid.js';
import { NONE, VIS_VISIBLE, type GameState, type Id, type Unit } from '../state/types.js';
import { recomputeVision } from '../vision/index.js';
import { cityAt, cityIndex } from '../cities/index.js';
import { cargoOf, unitDef, unitIndex, unitsAtIndex } from './index.js';
import { feelsZoc, zocBlocks, zocPlane } from './zoc.js';

/** Whether anyone but this civilisation has units or a city on a tile, in fact. */
export function occupiedByOthers(state: GameState, civId: Id, index: number): boolean {
  if (unitsAtIndex(state, index).some((u) => u.owner !== civId)) return true;
  const city = cityAt(state, state.map, index);
  return city !== null && city.owner !== civId;
}

/**
 * Whether a tile visibly holds someone else's units or city, as this civilisation
 * knows it — so a route plans around the army it can see, and not the one it cannot.
 */
export function foreignPresence(state: GameState, civId: Id, index: number, vision: Uint8Array | null): boolean {
  const level = vision ? vision[index]! : VIS_VISIBLE;
  if (level === VIS_VISIBLE) return occupiedByOthers(state, civId, index);
  const remembered = state.memory[civId]?.cities[index];
  return remembered !== undefined && remembered.owner !== civId;
}

export function isNeighbour(state: GameState, from: number, to: number): boolean {
  const buffer = new Int32Array(6);
  neighborsByDirection(state.map, from, buffer);
  for (let d = 0; d < 6; d++) if (buffer[d] === to) return true;
  return false;
}

/** Whether a tile is one of this civilisation's cities — the only land a ship may enter. */
export function isPort(state: GameState, civId: Id, index: number): boolean {
  const city = cityAt(state, state.map, index);
  return city !== null && city.owner === civId;
}

/**
 * Everything a route for this unit has to respect, in one place: what its owner knows
 * of the ground, the rivals it can see standing in the way, its own harbours if it is
 * a ship, and enemy zones of control if it is a soldier on foot. The goto command, a
 * goto re-routing mid-march and the reach overlay all ask here, so they agree.
 */
export function pathOptions(state: GameState, content: Content, unit: Unit, destination: number | null): PathOptions {
  const def = unitDef(content, unit);
  const vision = state.vision[unit.owner] ?? null;
  // Where others stand, gathered once per route rather than by scanning every unit for
  // every tile the search looks at (Phase 15). Nothing moves while a route is found.
  const others = new Set<number>();
  for (const u of Object.values(state.units)) if (u.owner !== unit.owner) others.add(unitIndex(state.map, u));
  for (const c of Object.values(state.cities)) if (c.owner !== unit.owner) others.add(cityIndex(state.map, c));
  const presence = (i: number): boolean => {
    const level = vision ? vision[i]! : VIS_VISIBLE;
    if (level === VIS_VISIBLE) return others.has(i);
    const remembered = state.memory[unit.owner]?.cities[i];
    return remembered !== undefined && remembered.owner !== unit.owner;
  };
  const options: PathOptions = {
    content,
    map: state.map,
    def,
    vision,
    // Rivals standing in the way, and land closed to this unit's owner (ADR-0060).
    avoid: (i) => (i !== destination && presence(i)) || !mayEnter(state, unit.owner, i),
  };
  if (def.domain === 'sea') options.port = (i) => isPort(state, unit.owner, i);
  if (feelsZoc(def)) {
    const plane = zocPlane(state, content, unit.owner);
    if (plane.some((v) => v === 1)) options.blockedStep = (from, to) => zocBlocks(state, content, unit.owner, plane, from, to);
  }
  return options;
}

/**
 * Move a unit one tile, pay for it, and refresh what its owner can see.
 *
 * A ship takes its cargo with it. A unit aboard that steps onto land has disembarked.
 */
export function applyMove(state: GameState, content: Content, unit: Unit, to: number, cost: number): GameEvent[] {
  const from = unitIndex(state.map, unit);
  const spent = movesSpent(unit.movesLeft, cost);

  const col = ((to % state.map.width) + state.map.width) % state.map.width;
  const row = (to - col) / state.map.width;
  const carried = cargoOf(state, unit);
  unit.col = col;
  unit.row = row;
  unit.movesLeft -= spent;
  for (const passenger of carried) {
    passenger.col = col;
    passenger.row = row;
  }

  const events: GameEvent[] = [{ t: 'UnitMoved', unitId: unit.id, from, to, cost: spent }];
  if (unit.aboard !== NONE && !content.terrain.get(state.map.terrain[to]!).water) {
    events.push({ t: 'UnitLanded', unitId: unit.id, shipId: unit.aboard, at: to });
    unit.aboard = NONE;
  }

  // Escorts that were alongside follow it, if they have the movement (ADR-0067). One that
  // cannot keeps its order and catches up when its next turn begins.
  for (const escort of state.units ? Object.values(state.units) : []) {
    if (escort.owner !== unit.owner || escort.orders?.kind !== 'escort' || escort.orders.target !== unit.id) continue;
    if (unitIndex(state.map, escort) !== from || escort.movesLeft <= 0) continue;
    const def = unitDef(content, escort);
    const step = moveCost(content, state.map, to, def, def.domain === 'sea' && isPort(state, escort.owner, to), from);
    if (step === IMPASSABLE || !canAfford(content, escort.movesLeft, step)) continue;
    events.push(...applyMove(state, content, escort, to, step));
  }

  const discovered = recomputeVision(state, content, unit.owner);
  if (discovered.length > 0) {
    events.push({ t: 'TilesDiscovered', civId: unit.owner, count: discovered.length });
  }
  return events;
}

/**
 * An escort that has fallen behind sails toward the ship it guards, as far as its
 * movement allows; one whose ship is gone stands down (ADR-0067).
 */
export function advanceEscort(state: GameState, content: Content, unit: Unit): GameEvent[] {
  const events: GameEvent[] = [];
  const order = unit.orders;
  if (!order || order.kind !== 'escort') return events;
  const target = state.units[order.target];
  if (!target || target.owner !== unit.owner) {
    unit.orders = null;
    events.push({ t: 'UnitOrderCleared', unitId: unit.id });
    return events;
  }
  const here = unitIndex(state.map, unit);
  const there = unitIndex(state.map, target);
  if (here === there || unit.movesLeft <= 0) return events;
  const route = findPath(pathOptions(state, content, unit, there), here, there);
  if (!route) return events;
  const def = unitDef(content, unit);
  for (const next of route) {
    const at = unitIndex(state.map, unit);
    const cost = moveCost(content, state.map, next, def, def.domain === 'sea' && isPort(state, unit.owner, next), at);
    if (cost === IMPASSABLE || !isNeighbour(state, at, next) || !canAfford(content, unit.movesLeft, cost)) break;
    if (occupiedByOthers(state, unit.owner, next) || !mayEnter(state, unit.owner, next)) break;
    events.push(...applyMove(state, content, unit, next, cost));
    if (next === there) break;
  }
  return events;
}

/**
 * Walk a unit along its goto order for as far as this turn's movement allows.
 *
 * Re-paths once when the next step turns out to be illegal. That is the ordinary way
 * an optimistic route through unexplored ground ends — the unit has just discovered
 * the mountains, or an enemy's zone of control — not an error. If there is still no
 * route the order is abandoned and an event says so, rather than the unit quietly
 * standing still for the rest of the game while the player wonders why.
 */
export function advanceGoto(state: GameState, content: Content, unit: Unit): GameEvent[] {
  const events: GameEvent[] = [];
  const order = unit.orders;
  if (!order || order.kind !== 'goto') return events;

  const def = unitDef(content, unit);
  let repathed = false;

  while (unit.movesLeft > 0 && order.step < order.path.length) {
    const next = order.path[order.step]!;
    const here = unitIndex(state.map, unit);
    const cost = moveCost(content, state.map, next, def, def.domain === 'sea' && isPort(state, unit.owner, next), here);

    // Out of movement for this tile is not a blocked path — it is simply the end of
    // the turn's walking. The order stays, and resumes next turn.
    if (cost !== IMPASSABLE && isNeighbour(state, here, next) && !canAfford(content, unit.movesLeft, cost)) break;

    // Someone else's units or city in the way. A goto never attacks and never pushes
    // past a rival at peace: it routes round them, or gives up and says so.
    // A border closed since the route was planned counts as occupied ground (ADR-0060).
    const occupied = occupiedByOthers(state, unit.owner, next) || !mayEnter(state, unit.owner, next);
    const zoc = feelsZoc(def) && zocBlocks(state, content, unit.owner, zocPlane(state, content, unit.owner), here, next);

    if (cost === IMPASSABLE || !isNeighbour(state, here, next) || occupied || zoc) {
      if (repathed || (occupied && next === order.destination)) {
        unit.orders = null;
        events.push({ t: 'GotoBlocked', unitId: unit.id, at: here });
        return events;
      }
      const vision = state.vision[unit.owner] ?? null;
      const destination = order.destination;
      const found = findPath(pathOptions(state, content, unit, destination), here, destination);
      const fresh = found && foreignPresence(state, unit.owner, destination, vision) ? found.slice(0, -1) : found;
      if (!fresh || fresh.length === 0) {
        unit.orders = null;
        events.push({ t: 'GotoBlocked', unitId: unit.id, at: here });
        return events;
      }
      order.path = fresh;
      order.step = 0;
      repathed = true;
      continue;
    }

    events.push(...applyMove(state, content, unit, next, cost));
    order.step++;
  }

  if (order.step >= order.path.length) {
    unit.orders = null;
    events.push({ t: 'UnitOrderCompleted', unitId: unit.id });
  }
  return events;
}
